import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  ControlPolicyStatusToken,
  EmptyState,
  Field,
  PageHeader,
  Select,
  Spinner,
  Textarea,
} from '@aigovops/design-system';
import type {
  ContentBatchRow,
  ProposedChange,
  ProposedChangeSeverity,
  ProposedChangeSourceKind,
  ProposedChangeStatus,
  ProposedChangeTarget,
  RuleDomain,
  RiskVector,
} from '@aigovops/core';
import {
  PROPOSAL_PROGRAMS,
  PROPOSAL_PROGRAM_LABELS,
  describeProposal,
  programForProposal,
  separateApprovalLabel,
  batchVocabularyLabel,
  type ProposalProgramKey,
  RULE_DOMAIN_LABELS,
  RULE_DOMAINS,
  RISK_VECTOR_LABELS,
  RISK_VECTOR_CATEGORIES,
  RISK_VECTORS,
} from '@aigovops/core';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { queryKeys } from '../lib/query-keys';
import { ProposeSourceForm } from '../components/ProposeSourceForm';
import { NeedsAuthoringLane } from '../components/NeedsAuthoringLane';
import {
  ControlImpactPanel,
  ControlImpactStatsLine,
  FiledNotice,
  FiledSummary,
  OverrideNote,
  carryTicks,
  changeSearch,
  confirmLabel,
  defaultTicks,
  normalizeControlImpact,
  outcomeForTicks,
  sameTicks,
  willFile,
  type FiledNoticeData,
} from '../components/queue/ControlImpactPanel';
import { useAnnounce } from '../components/today/shared';
import { fmtDate } from '../lib/format';
import {
  ApiError,
  approveContentBatch,
  approveProposedChange,
  correctProposalJurisdiction,
  retargetProposedChange,
  untargetProposedChange,
  applyProposedChange,
  createProposedChange,
  getAuthoringSummary,
  getControlImpactStats,
  getProposedChange,
  getQueueSummary,
  listContentBatches,
  listProposedChanges,
  ratifyProposedChange,
  rejectProposedChange,
  rerunControlImpact,
  setProposalSourceUrl,
  type ControlImpactView,
  type QueueSummary,
} from '../lib/api';
import { intervalWords, type ImpactOutcome } from '@aigovops/core';

/** V112-4-7: the Control impact choice a legal row's approve sends (ADR-0098). */
type ControlImpactDecision = { control_impact_outcome: ImpactOutcome; control_impact_kept?: number[] };

/**
 * Editorial queue — the review dashboard for every content mutation.
 *
 * Reads from the /api/proposed-changes surface added by the queue-augmentation
 * commit. Default view is pending items sorted by SLA priority (breaches
 * first, then critical, then by deadline). The reviewer identity for
 * approve/reject actions is the signed-in user's email so the queue's audit
 * trail carries a real person, not 'seed:v0.1'.
 *
 * The page uses no external data-fetching library (the SPA convention is
 * useAsync from ../lib/useAsync); mutations trigger a summary + list refetch
 * rather than optimistic updates — this queue is single-reviewer today and
 * the round-trip is fast enough that optimistic UI would be premature
 * complexity.
 */

type StatusFilter = ProposedChangeStatus | 'all';
type SeverityFilter = ProposedChangeSeverity | 'all';
type SourceFilter = ProposedChangeSourceKind | 'all';
type TargetFilter = ProposedChangeTarget | 'all';
type OrderFilter = 'created_desc' | 'sla_priority';
type ProgramFilter = ProposalProgramKey | 'all';
const PROGRAM_KEYS = [...PROPOSAL_PROGRAMS.map((p) => p.key), 'other'] as ProposalProgramKey[];

// Window during which a proposal from a recently-approved research_source
// is flagged with a "new source" chip. 30 days matches the editorial
// probation intent from ADR-0002: after a month of clean output an
// editor can reasonably trust the source without special attention.
const NEW_SOURCE_WINDOW_MS = 30 * 24 * 60 * 60 * 1000;

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: 'pending', label: 'Pending review' },
  { value: 'approved', label: 'Approved' },
  { value: 'applied', label: 'Applied' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'superseded', label: 'Superseded' },
  { value: 'all', label: 'All statuses' },
];
const SEVERITY_OPTIONS: { value: SeverityFilter; label: string }[] = [
  { value: 'all', label: 'Any severity' },
  { value: 'critical', label: 'Critical (48h)' },
  { value: 'elevated', label: 'Elevated (3d)' },
  { value: 'routine', label: 'Routine (7d)' },
];
const SOURCE_OPTIONS: { value: SourceFilter; label: string }[] = [
  { value: 'all', label: 'Any source' },
  { value: 'research_pipeline', label: 'Research pipeline' },
  { value: 'admin_edit', label: 'Admin edit' },
  { value: 'seed', label: 'Seed (bootstrap)' },
  { value: 'partner_import', label: 'Partner import' },
];
// Target-type filter — most editors work one target at a time. Only the
// three highest-traffic content targets plus research_source appear as
// first-class options; 'all' remains the default.
const TARGET_OPTIONS: { value: TargetFilter; label: string }[] = [
  { value: 'all', label: 'Any target' },
  { value: 'research_source', label: 'Research source (new)' },
  { value: 'risk_item', label: 'Risk item' },
  { value: 'legislation', label: 'Legislation' },
  { value: 'control_spec', label: 'Control spec' },
  { value: 'control', label: 'Control' },
  { value: 'framework', label: 'Framework' },
  { value: 'vendor', label: 'Vendor' },
  { value: 'jurisdiction_rule', label: 'Jurisdiction rule' },
  // V104-G-12 / ADR-0077: a jurisdiction's own role or risk-tier word.
  { value: 'jurisdiction_term', label: 'Jurisdiction term' },
  // V117-3-5 / ADR-0112 §6: a control's reviewed guide.
  { value: 'control_guide', label: 'Control guide' },
];
const DOMAIN_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Any domain' },
  ...RULE_DOMAINS.map((d) => ({ value: d, label: RULE_DOMAIN_LABELS[d] })),
];
const RISK_VECTOR_OPTIONS: { value: string; label: string }[] = [
  { value: 'all', label: 'Any risk vector' },
  ...RISK_VECTORS.map((v) => ({ value: v, label: RISK_VECTOR_LABELS[v] })),
];
const ORDER_OPTIONS: { value: OrderFilter; label: string }[] = [
  { value: 'sla_priority', label: 'Priority: breaches → critical → deadline' },
  { value: 'created_desc', label: 'Recently created' },
];

const SEVERITY_TONE: Record<ProposedChangeSeverity, { bg: string; fg: string; label: string }> = {
  critical: { bg: 'var(--critical)', fg: 'var(--text-inverse)', label: 'critical' },
  elevated: { bg: 'var(--warning)', fg: 'var(--text-inverse)', label: 'elevated' },
  routine: { bg: 'var(--surface-elevated, var(--surface))', fg: 'var(--text-muted)', label: 'routine' },
};

export function Queue() {
  const { user } = useAuth();
  const [status, setStatus] = useState<StatusFilter>('pending');
  const [severity, setSeverity] = useState<SeverityFilter>('all');
  const [source, setSource] = useState<SourceFilter>('all');
  const [target, setTarget] = useState<TargetFilter>('all');
  const [breachingOnly, setBreachingOnly] = useState(false);
  const [duplicatesOnly, setDuplicatesOnly] = useState(false);
  const [domainFilter, setDomainFilter] = useState<string>('all');
  const [riskVectorFilter, setRiskVectorFilter] = useState<string>('all');
  const [order, setOrder] = useState<OrderFilter>('sla_priority');
  const [actionState, setActionState] = useState<{
    changeId: string;
    kind: 'approve' | 'reject';
    notes: string;
  } | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  // Security review M1: approving a rule quoted from law text you supplied yourself takes one more confirmation.
  const [selfSource, setSelfSource] = useState<{ changeId: string; notes: string; title: string; ci?: ControlImpactDecision } | null>(null);
  // V112-4-7: what the last applied law's Control impact filed (or skipped), with links to the new proposals.
  const [filedNotice, setFiledNotice] = useState<FiledNoticeData | null>(null);
  const [busyChangeId, setBusyChangeId] = useState<string | null>(null);
  const [proposeOpen, setProposeOpen] = useState(false);
  // v10.3: Batches view — content batches in approval order. `batchFilter`
  // narrows the Rows view to one batch ("Review rows").
  // Batch sign-off (owner decision 2026-10-08): editors start in Batches;
  // Rows is for scanner/AI proposals and individual checks.
  // v10.4 Track G: `?lane=authoring` (the link from Today) opens the Needs authoring lane.
  const [searchParams, setSearchParams] = useSearchParams();
  const [view, setViewState] = useState<'rows' | 'batches' | 'authoring'>(() =>
    searchParams.get('lane') === 'authoring'
      ? 'authoring'
      : searchParams.get('change')
        ? 'rows'
        : !user || user.role === 'admin' || user.role === 'editor' ? 'batches' : 'rows',
  );
  const setView = useCallback((v: 'rows' | 'batches' | 'authoring') => {
    setViewState(v);
    if (v !== 'authoring' && searchParams.get('lane')) {
      const next = new URLSearchParams(searchParams);
      next.delete('lane');
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  const [batchFilter, setBatchFilter] = useState<{ key: string; label: string } | null>(null);
  const [program, setProgram] = useState<ProgramFilter>('all');
  // V112-4-7: `?change=<id>` shows that one proposal (the links under "Filed: …").
  const focusChangeId = searchParams.get('change');
  useEffect(() => { if (focusChangeId) setViewState('rows'); }, [focusChangeId]);
  const clearFocus = useCallback(() => {
    const next = new URLSearchParams(searchParams);
    next.delete('change');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams]);

  const filterParams = useMemo(() => ({
    status: status === 'all' ? undefined : status,
    severity: severity === 'all' ? undefined : severity,
    source_kind: source === 'all' ? undefined : source,
    target_type: target === 'all' ? undefined : target,
    domain: domainFilter === 'all' ? undefined : domainFilter,
    risk_vector: riskVectorFilter === 'all' ? undefined : riskVectorFilter,
    breaching_sla: breachingOnly || undefined,
    possible_duplicate: duplicatesOnly || undefined,
    order,
    batch: batchFilter?.key,
    program: program === 'all' ? undefined : program,
  }), [status, severity, source, target, domainFilter, riskVectorFilter, breachingOnly, duplicatesOnly, order, batchFilter, program]);
  // V116-4-6: the Rows view loads ROWS_PAGE at a time and says when more match (one extra row is asked for to know).
  // The limit belongs to one filter set and view: changing either starts again at one page, in the same render.
  const rowsKey = JSON.stringify([filterParams, view]);
  const [rowsState, setRowsState] = useState({ key: rowsKey, limit: ROWS_PAGE });
  if (rowsState.key !== rowsKey) setRowsState({ key: rowsKey, limit: ROWS_PAGE });
  const rowLimit = rowsState.key === rowsKey ? rowsState.limit : ROWS_PAGE;
  const listParams = useMemo(() => ({ ...filterParams, limit: rowLimit + 1 }), [filterParams, rowLimit]);

  const summaryQuery = useQuery<QueueSummary>({
    queryKey: queryKeys.queueSummary(),
    queryFn: () => getQueueSummary(),
  });
  const summary = {
    data: summaryQuery.data ?? null,
    error: summaryQuery.error?.message ?? null,
    loading: summaryQuery.isLoading,
    refetch: summaryQuery.refetch,
  };

  const listQuery = useQuery({
    queryKey: queryKeys.queue(listParams as unknown as Record<string, unknown>),
    queryFn: () => listProposedChanges(listParams),
    enabled: !focusChangeId && view === 'rows',
    // "Show more" keeps the rows on screen while the next page loads; a filter change (rowLimit back to one page) never
    // shows the previous filter's rows.
    placeholderData: (prev) => (rowLimit > ROWS_PAGE ? keepPreviousData(prev) : undefined),
  });
  const focusQuery = useQuery({
    queryKey: queryKeys.queue({ change: focusChangeId }),
    queryFn: async () => ({ proposed_changes: [(await getProposedChange(focusChangeId!)).change] }),
    enabled: !!focusChangeId,
  });
  const activeList = focusChangeId ? focusQuery : listQuery;
  // A filed follow-on links back to the law it came from (impact.source_change_id).
  const focusRow = focusQuery.data?.proposed_changes[0];
  const sourceChangeId = focusChangeId && focusRow ? parseImpact(focusRow.impact).source_change_id ?? null : null;
  const sourceLawQuery = useQuery({
    queryKey: queryKeys.queue({ change: sourceChangeId, role: 'source' }),
    queryFn: () => getProposedChange(sourceChangeId!),
    enabled: !!sourceChangeId,
    retry: false,
  });
  const list = {
    data: activeList.data ?? null,
    error: activeList.error?.message ?? null,
    loading: activeList.isLoading,
    refetch: activeList.refetch,
  };

  // v10.4 Track G: count for the Needs authoring lane button (laws whose rule draft failed).
  const authoringQuery = useQuery({ queryKey: queryKeys.authoringSummary(), queryFn: () => getAuthoringSummary(), retry: false });
  // The button shows while anything is unresolved; its count is what an editor can act on now
  // (items parked waiting for a rule field are not counted).
  const authoringCount = authoringQuery.data?.unresolved ?? 0;
  const authoringOpen = authoringQuery.data?.open ?? 0;

  const reviewerId = user?.email ?? 'unknown-reviewer';
  // Editorial-queue writes (approve/reject/apply/propose/deprecate-source)
  // are gated to admin + editor. Viewers see the list but no action UI.
  // Backend enforces the same in proposed-changes.ts canWrite(). When
  // there is no user (mock-auth mode, contributor dev), we default open —
  // matches the API mock-auth escape so local dev keeps working.
  const canWrite = !user || user.role === 'admin' || user.role === 'editor';

  const queryClient = useQueryClient();
  const [live, announce] = useAnnounce();

  // V116-4-6: "Show more" moves focus to the first new row once it loads and announces the new count. A failed page
  // rolls back to the rows already shown (cached) and says so in the notice.
  const loadingMore = listQuery.isPlaceholderData;
  const firstNewRow = useRef<string | null>(null);
  const [moreFailed, setMoreFailed] = useState(false);
  const listRows = listQuery.data?.proposed_changes;
  const cutOff = !focusChangeId && !!listRows && (listRows.length > rowLimit || loadingMore);
  const shownRows = loadingMore ? rowLimit - ROWS_PAGE : rowLimit;
  const showMore = useCallback(() => {
    firstNewRow.current = listRows?.[rowLimit]?.change_id ?? null;
    setMoreFailed(false);
    setRowsState({ key: rowsKey, limit: rowLimit + ROWS_PAGE });
  }, [listRows, rowLimit, rowsKey]);
  useEffect(() => {
    if (listQuery.isError && rowLimit > ROWS_PAGE) {
      firstNewRow.current = null;
      setMoreFailed(true);
      setRowsState({ key: rowsKey, limit: rowLimit - ROWS_PAGE });
    }
  }, [listQuery.isError, rowLimit, rowsKey]);
  const announcedRows = useRef<string | null>(null);
  useEffect(() => {
    if (loadingMore || !listRows || listQuery.isFetching || focusChangeId) return;
    const id = firstNewRow.current;
    if (id) { firstNewRow.current = null; document.getElementById(`row-title-${id}`)?.focus(); }
    const at = `${rowsKey}:${rowLimit}`;
    if (announcedRows.current === at) return;
    announcedRows.current = at;
    if (listRows.length > rowLimit) announce(`Showing the first ${rowLimit.toLocaleString()}. More match these filters.`);
    else if (id) announce(`Showing all ${listRows.length.toLocaleString()}.`);
  }, [loadingMore, listRows, listQuery.isFetching, focusChangeId, rowsKey, rowLimit, announce]);
  const reviewFilters = useCallback(() => {
    const bar = document.getElementById('queue-filters');
    bar?.scrollIntoView?.({ block: 'start' });
    bar?.querySelector<HTMLElement>('select, input, button')?.focus();
  }, []);
  const reviewByBatch = useCallback(() => {
    setView('batches');
    setTimeout(() => document.getElementById('queue-view-batches')?.focus(), 0);
  }, [setView]);
  const refetchAll = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: queryKeys.queueSummary() });
    queryClient.invalidateQueries({ queryKey: ['queue'] });
    queryClient.invalidateQueries({ queryKey: ['queue-batches'] });
    queryClient.invalidateQueries({ queryKey: ['authoring-items'] });
  }, [queryClient]);

  const decide = useCallback(
    async (changeId: string, kind: 'approve' | 'reject', notes: string, confirmSelfSource = false, ci?: ControlImpactDecision) => {
      setBusyChangeId(changeId);
      setActionError(null);
      setFiledNotice(null);
      const row = queryClient.getQueriesData<{ proposed_changes: ProposedChange[] }>({ queryKey: ['queue'] })
        .flatMap(([, d]) => d?.proposed_changes ?? []).find((c) => c.change_id === changeId);
      try {
        const decision = {
          reviewer: reviewerId,
          review_notes: notes || undefined,
          ...(confirmSelfSource ? { confirm_self_supplied_source: true } : {}),
          // V112-4-6/4-7 (ADR-0098): the Control impact travels as fields, not as a note prefix.
          ...(kind === 'approve' && ci ? ci : {}),
        };
        if (kind === 'approve') {
          try {
            await approveProposedChange(changeId, decision);
          } catch (err) {
            if (err instanceof ApiError && err.status === 409 && (err.body as { error?: string } | null)?.error === 'confirm_self_supplied_source') {
              setSelfSource({ changeId, notes, title: row ? describeProposal(row) : 'this rule', ci });
              return;
            }
            throw err;
          }
          setSelfSource(null);
          // Apply immediately on approve — that's the queue's happy path.
          // A future refinement could split apply into its own step.
          const applied = await applyProposedChange(changeId, {});
          // V112-4-7: an applied law reports the follow-on proposals its Control impact filed.
          const filing = applied?.control_impact;
          if (filing && (filing.filed?.length || filing.skipped?.length)) {
            setFiledNotice({
              title: row ? describeProposal(row) : 'Law applied',
              filing: { filed: filing.filed ?? [], skipped: filing.skipped ?? [] },
              duties: row ? parseImpact(row.impact).control_impact?.duties ?? [] : [],
            });
          }
        } else {
          await rejectProposedChange(changeId, decision);
        }
        setActionState(null);
        refetchAll();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
        // The approve may have landed even though apply failed; refetch so the
        // row leaves the pending list and shows as approved with "Apply now".
        refetchAll();
      } finally {
        setBusyChangeId(null);
      }
    },
    [reviewerId, refetchAll, queryClient],
  );

  // V112-4-7: re-run the Control impact analysis of a pending law, then refresh the row.
  const rerunImpact = useCallback(
    async (changeId: string) => {
      const res = await rerunControlImpact(changeId);
      await queryClient.refetchQueries({ queryKey: ['queue'] });
      return normalizeControlImpact(res.control_impact);
    },
    [queryClient],
  );
  // V112-4-7 (D-BL45-3): add the statute's own link (the analysis re-runs and reads it), then refresh the row.
  const setSourceUrl = useCallback(
    async (changeId: string, url: string) => {
      await setProposalSourceUrl(changeId, url);
      await queryClient.refetchQueries({ queryKey: ['queue'] });
    },
    [queryClient],
  );
  // V112-4-7: correct a pending law's jurisdiction (the analysis re-runs on the server), then refresh the row.
  const correctJurisdiction = useCallback(
    async (changeId: string, jurisdiction: string) => {
      await correctProposalJurisdiction(changeId, jurisdiction);
      await queryClient.refetchQueries({ queryKey: ['queue'] });
    },
    [queryClient],
  );

  // Retry the apply step for a row an earlier apply failure left approved.
  const applyApproved = useCallback(
    async (changeId: string) => {
      setBusyChangeId(changeId);
      setActionError(null);
      try {
        await applyProposedChange(changeId, {});
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        refetchAll();
        setBusyChangeId(null);
      }
    },
    [refetchAll],
  );

  // Approved rows that never applied are invisible under the default
  // "Pending review" filter — surface them so they are not stranded.
  const approvedQuery = useQuery({
    queryKey: queryKeys.queue({ status: 'approved', limit: 200 }),
    queryFn: () => listProposedChanges({ status: 'approved', limit: 200 }),
    enabled: canWrite,
  });
  const approvedCount = approvedQuery.data?.proposed_changes.length ?? 0;

  // V112-4-7 (BL-45 acceptance 4): how often the suggested Control impact is kept.
  const ciStatsQuery = useQuery({
    queryKey: queryKeys.controlImpactStats(),
    queryFn: () => getControlImpactStats(),
    enabled: canWrite,
    retry: false,
  });

  // Inline "Deprecate source" — the reviewer, while looking at a content
  // proposal from a new source, decides the source itself is untrustworthy
  // and kills it in one action. Provenance flows through proposed_changes:
  // raise a research_source deprecate proposal, approve+apply it immediately.
  // The chained round-trips are three calls today — acceptable for a
  // low-frequency action; can collapse into a single POST if it becomes hot.
  const deprecateSource = useCallback(
    async (change: ProposedChange, reason: string) => {
      if (!change.source_id || !change.source_name) return;
      setBusyChangeId(change.change_id);
      setActionError(null);
      try {
        const raised = await createProposedChange({
          target_type: 'research_source',
          target_id: change.source_id,
          change_type: 'deprecate',
          proposed_state: {},
          ai_rationale: reason,
        });
        const decision = { reviewer: reviewerId, review_notes: reason };
        await approveProposedChange(raised.change_id, decision);
        await applyProposedChange(raised.change_id, {});
        refetchAll();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyChangeId(null);
      }
    },
    [reviewerId, refetchAll],
  );

  // One-click ratification — replaces the placeholder seed:v0.1 reviewer
  // stamp on applied+seed rows with the current admin's email. No
  // register mutation; audit-trail-only. Backend refuses on non-seed rows.
  const ratify = useCallback(
    async (changeId: string) => {
      setBusyChangeId(changeId);
      setActionError(null);
      try {
        await ratifyProposedChange(changeId, { review_notes: 'Ratified via /app/queue.' });
        refetchAll();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyChangeId(null);
      }
    },
    [refetchAll],
  );

  const retarget = useCallback(
    async (changeId: string, targetName: string, undo = false) => {
      setBusyChangeId(changeId);
      setActionError(null);
      try {
        await (undo ? untargetProposedChange(changeId) : retargetProposedChange(changeId));
        // Wait for the row to re-render before moving focus into it.
        await queryClient.refetchQueries({ queryKey: ['queue'] });
        refetchAll();
        announce(undo
          ? `Back to adding a new item. ${targetName} stays as it is.`
          : `Now an update of ${targetName}. Review what changes, then approve.`);
        setTimeout(() => (document.getElementById(undo ? `retarget-${changeId}` : `diff-${changeId}`) ?? document.getElementById(`row-title-${changeId}`))?.focus(), 0);
      } catch (err) {
        // A 409 means the row moved on (someone else acted): reload so the stale button goes away.
        await queryClient.refetchQueries({ queryKey: ['queue'] }).catch(() => undefined);
        setActionError(`Couldn’t change this proposal. ${err instanceof Error ? err.message : ''} The queue has been reloaded.`.trim());
        setTimeout(() => document.getElementById(`row-title-${changeId}`)?.focus(), 0);
      } finally {
        setBusyChangeId(null);
      }
    },
    [refetchAll, queryClient, announce],
  );

  // Combined action for the "this content is bad AND its source is
  // untrustworthy" flow — rejects the current proposal, then deprecates
  // the source, both with the same rationale. Runs sequentially so a
  // failure on the source step still leaves the reject applied; the user
  // sees the error and can retry the deprecate via the standalone button.
  const rejectAndDeprecate = useCallback(
    async (change: ProposedChange, reason: string) => {
      if (!change.source_id) return;
      setBusyChangeId(change.change_id);
      setActionError(null);
      try {
        const decision = { reviewer: reviewerId, review_notes: reason };
        await rejectProposedChange(change.change_id, decision);
        const raised = await createProposedChange({
          target_type: 'research_source',
          target_id: change.source_id,
          change_type: 'deprecate',
          proposed_state: {},
          ai_rationale: reason,
        });
        await approveProposedChange(raised.change_id, decision);
        await applyProposedChange(raised.change_id, {});
        setActionState(null);
        refetchAll();
      } catch (err) {
        setActionError(err instanceof Error ? err.message : String(err));
      } finally {
        setBusyChangeId(null);
      }
    },
    [reviewerId, refetchAll],
  );

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {live}
      <PageHeader
        title="Editorial queue"
        subtitle={
          summary.data
            ? `${summary.data.pending_total} pending · ${summary.data.breaching_sla} breaching SLA · ${summary.data.seed_awaiting_ratification} seeded items awaiting your ratification`
            : 'Every content mutation flows through here — pipeline, admin edits, seed backfill.'
        }
        right={
          canWrite && !proposeOpen ? (
            <Button variant="secondary" compact onClick={() => setProposeOpen(true)}>
              + Propose new source
            </Button>
          ) : null
        }
      />

      {!canWrite ? (
        <Alert variant="info">
          Read-only view — an Editor or Admin role is required to approve, reject, or propose changes.
        </Alert>
      ) : null}

      {proposeOpen ? (
        <ProposeSourceForm
          onDone={() => {
            setProposeOpen(false);
            setTarget('research_source');
            refetchAll();
          }}
          onCancel={() => setProposeOpen(false)}
        />
      ) : null}

      {summary.data ? <SummaryStrip summary={summary.data} /> : null}
      {canWrite ? <ControlImpactStatsLine stats={ciStatsQuery.data} /> : null}

      {approvedCount > 0 && !(view === 'rows' && status === 'approved') ? (
        <Alert variant="warning">
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            {approvedCount} approved {approvedCount === 1 ? 'change was' : 'changes were'} not applied
            <Button compact variant="secondary" onClick={() => { setBatchFilter(null); setStatus('approved'); setView('rows'); }}>
              Show them
            </Button>
          </span>
        </Alert>
      ) : null}

      {canWrite || authoringCount > 0 || view === 'authoring' ? (
        <div role="group" aria-label="Queue view" style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
          <Button compact variant={view === 'rows' ? 'primary' : 'secondary'} aria-pressed={view === 'rows'} onClick={() => setView('rows')}>
            Rows
          </Button>
          {canWrite ? (
            <Button id="queue-view-batches" compact variant={view === 'batches' ? 'primary' : 'secondary'} aria-pressed={view === 'batches'} onClick={() => setView('batches')}>
              Batches
            </Button>
          ) : null}
          {authoringCount > 0 || view === 'authoring' ? (
            <Button compact variant={view === 'authoring' ? 'primary' : 'secondary'} aria-pressed={view === 'authoring'} onClick={() => setView('authoring')}>
              Needs authoring{authoringOpen > 0 ? ` (${authoringOpen})` : ''}
            </Button>
          ) : null}
        </div>
      ) : null}

      {view === 'authoring' ? (
        <NeedsAuthoringLane canWrite={canWrite} onBack={() => setView(canWrite ? 'batches' : 'rows')} />
      ) : view === 'batches' && canWrite ? (
        <BatchesPanel
          focusKey={searchParams.get('rule_set')}
          returnTo={safeReturn(searchParams.get('return'))}
          onReviewRows={(key, label) => {
            setBatchFilter({ key, label });
            setStatus('pending');
            setView('rows');
          }}
          onChanged={refetchAll}
          onShowSourceActivations={() => {
            setBatchFilter(null);
            setProgram('all');
            setTarget('research_source');
            setStatus('pending');
            setView('rows');
          }}
        />
      ) : (
      <>
      {batchFilter ? (
        <Alert variant="info">
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            Showing batch {batchFilter.label}
            <Button compact variant="secondary" onClick={() => setBatchFilter(null)}>Show all rows</Button>
            <Button compact variant="secondary" onClick={() => setView('batches')}>Back to batches</Button>
          </span>
        </Alert>
      ) : null}

      {focusChangeId ? null : (
      <div id="queue-filters">
      <FilterBar
        status={status}
        severity={severity}
        source={source}
        target={target}
        domainFilter={domainFilter}
        riskVectorFilter={riskVectorFilter}
        breachingOnly={breachingOnly}
        duplicatesOnly={duplicatesOnly}
        order={order}
        onStatus={setStatus}
        onSeverity={setSeverity}
        onSource={setSource}
        onTarget={setTarget}
        onDomainFilter={setDomainFilter}
        onRiskVectorFilter={setRiskVectorFilter}
        onBreachingOnly={setBreachingOnly}
        onDuplicatesOnly={setDuplicatesOnly}
        onOrder={setOrder}
        program={program}
        programCounts={summary.data?.by_program ?? null}
        onProgram={setProgram}
      />
      </div>
      )}

      {focusChangeId ? (
        <Alert variant="info">
          <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            Showing one proposal
            {sourceChangeId ? (
              <span>
                {'Filed from '}
                <Link to={{ search: changeSearch(searchParams.toString(), sourceChangeId) }}>
                  {sourceLawQuery.data ? describeProposal(sourceLawQuery.data.change) : 'the law it came from'}
                </Link>
              </span>
            ) : null}
            <Button compact variant="secondary" onClick={clearFocus}>Show all rows</Button>
          </span>
        </Alert>
      ) : null}
      {actionError ? <Alert variant="error">{actionError}</Alert> : null}
      <FiledNotice notice={filedNotice} onDismiss={() => setFiledNotice(null)} />
      {selfSource ? (
        <SelfSourceConfirm
          title={selfSource.title}
          busy={busyChangeId === selfSource.changeId}
          onConfirm={() => void decide(selfSource.changeId, 'approve', selfSource.notes, true, selfSource.ci)}
          onCancel={() => setSelfSource(null)}
        />
      ) : null}

      {list.loading ? (
        <Spinner label="Loading queue" />
      ) : list.error ? (
        <Alert variant="error">{list.error}</Alert>
      ) : !list.data || list.data.proposed_changes.length === 0 ? (
        <EmptyState
          title="Nothing to review"
          description={
            status === 'pending'
              ? 'No pending changes match your filters. Try widening severity, source, or status.'
              : 'No changes match this filter combination.'
          }
        />
      ) : (
        <>
        {cutOff ? <p style={META_LINE}>Showing {shownRows.toLocaleString()} · more match these filters</p> : null}
        <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-3)' }}>
          {list.data.proposed_changes.slice(0, shownRows).map((c) => (
            <QueueRow
              key={c.change_id}
              change={c}
              busy={busyChangeId === c.change_id}
              canWrite={canWrite}
              actionState={actionState?.changeId === c.change_id ? actionState : null}
              onOpen={(kind) =>
                setActionState({ changeId: c.change_id, kind, notes: '' })
              }
              onCancel={() => setActionState(null)}
              onNotes={(notes) =>
                setActionState((s) => (s && s.changeId === c.change_id ? { ...s, notes } : s))
              }
              onConfirm={(kind, notes, ci) => decide(c.change_id, kind, notes, false, ci)}
              onRerunImpact={() => rerunImpact(c.change_id)}
              onCorrectJurisdiction={(j) => correctJurisdiction(c.change_id, j)}
              onSetSourceUrl={(u) => setSourceUrl(c.change_id, u)}
              announce={announce}
              onDeprecateSource={(reason) => deprecateSource(c, reason)}
              onRejectAndDeprecate={(reason) => rejectAndDeprecate(c, reason)}
              onRatify={() => ratify(c.change_id)}
              onApply={() => applyApproved(c.change_id)}
              onRetarget={(name, undo) => retarget(c.change_id, name, undo)}
            />
          ))}
        </ol>
        {cutOff || moreFailed ? (
          <RowsCutOff
            shown={shownRows}
            loading={loadingMore}
            failed={moreFailed}
            onMore={showMore}
            onFilters={reviewFilters}
            // The Batches view holds every pending row; only an unfiltered pending list is sent there.
            onBatches={canWrite && status === 'pending' && !batchFilter ? reviewByBatch : undefined}
          />
        ) : null}
        </>
      )}
      </>
      )}
    </div>
  );
}

const SPOT_CHECK_SIZE = 10;
/** V116-4-6: Rows view page size, and the most it loads (the list API's cap). */
const ROWS_PAGE = 200;
const ROWS_MAX = 1000;

const META_LINE = { margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' } as const;

/** V116-4-6: the Rows view says when it stops short, with the way to see the rest. */
function RowsCutOff(props: {
  shown: number; loading: boolean; failed: boolean;
  onMore: () => void; onFilters: () => void; onBatches?: () => void;
}) {
  const { shown, loading, failed, onMore, onFilters, onBatches } = props;
  const atMax = shown >= ROWS_MAX && !loading;
  const first = `Showing the first ${shown.toLocaleString()}.`;
  const text = failed ? `${first} Could not load more.`
    : !atMax ? `${first} More match these filters.`
    : onBatches ? `${first} Review by batch or narrow the filters to see the rest.`
    : `${first} Narrow the filters to see the rest.`;
  return (
    <div style={{ ...META_LINE, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 'var(--space-3)', marginTop: 'var(--space-6)' }}>
      <span>{text}</span>
      {failed || !atMax
        ? <Button variant="secondary" compact aria-disabled={loading || undefined} onClick={loading ? undefined : onMore}>{loading ? 'Loading…' : failed ? 'Try again' : 'Show more'}</Button>
        : onBatches ? <Button variant="secondary" compact onClick={onBatches}>Review by batch</Button>
        : <Button variant="secondary" compact onClick={onFilters}>Change filters</Button>}
    </div>
  );
}

/**
 * Batch sign-off (owner decision 2026-10-08): before a batch is approved the
 * reviewer reads a sample spread across it, in plain words. The sample size
 * is recorded in the approval note.
 */
/** Security review M1: the proposal was quoted from statute text an editor supplied. */
export function editorSuppliedText(change: Pick<ProposedChange, 'impact'>): boolean {
  try {
    const impact = typeof change.impact === 'string' ? JSON.parse(change.impact) : change.impact;
    return (impact as { statute_text_origin?: unknown } | null)?.statute_text_origin === 'editor_supplied';
  } catch {
    return false;
  }
}

/**
 * Security review M1: the approver supplied the law text this rule quotes. With
 * one editor this is an override, not a refusal: say so plainly and confirm once.
 */
function SelfSourceConfirm({ title, busy, onConfirm, onCancel }: { title: string; busy: boolean; onConfirm: () => void; onCancel: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    ref.current?.focus();
  }, []);
  return (
    <div ref={ref} tabIndex={-1} role="alertdialog" aria-labelledby="self-source-h" aria-describedby="self-source-d"
      style={{ border: '1px solid var(--warning)', borderRadius: 'var(--radius-card)', background: 'var(--surface)', padding: 'var(--space-3)', display: 'grid', gap: 'var(--space-2)' }}>
      <h2 id="self-source-h" style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>You supplied the law text for this rule</h2>
      <p id="self-source-d" style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
        {title}. Approve it only if you checked that text against the official source.
      </p>
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Button compact variant="primary" aria-disabled={busy || undefined} onClick={() => !busy && onConfirm()}>
          I checked it — approve
        </Button>
        <Button compact variant="secondary" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

function SpotCheck({ batch, busy, onApprove, onCancel }: {
  batch: ContentBatchRow;
  busy: boolean;
  onApprove: (sampled: number) => void;
  onCancel: () => void;
}) {
  const sampleQuery = useQuery({
    queryKey: ['queue', { spot_check: batch.group_key }],
    // Open rows are pending ones plus approved ones an earlier apply left
    // behind (the batch re-run retries those), so sample both.
    queryFn: async () => {
      const [pending, approved] = await Promise.all([
        listProposedChanges({ status: 'pending', batch: batch.group_key, order: 'created_desc', limit: 200 }),
        listProposedChanges({ status: 'approved', batch: batch.group_key, order: 'created_desc', limit: 200 }),
      ]);
      return [...pending.proposed_changes, ...approved.proposed_changes];
    },
  });
  const rows = sampleQuery.data ?? [];
  // Spread the sample across the batch instead of taking the first few.
  const step = Math.max(1, Math.floor(rows.length / SPOT_CHECK_SIZE));
  const sample = rows.filter((_, i) => i % step === 0).slice(0, SPOT_CHECK_SIZE);

  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
      {sampleQuery.isLoading ? (
        <Spinner label="Loading sample" />
      ) : sampleQuery.error ? (
        <Alert variant="error">{sampleQuery.error.message}</Alert>
      ) : sample.length === 0 ? (
        <Alert variant="warning">
          No rows to sample: this batch has nothing left to apply. Refresh the page; if it still shows, use Review rows.
        </Alert>
      ) : (
        <ol aria-label={`Sample of ${batch.label}`} style={{ margin: 0, paddingLeft: 'var(--space-4)', display: 'grid', gap: 'var(--space-1)' }}>
          {sample.map((c) => (
            <li key={c.change_id} style={{ color: 'var(--text)', fontSize: 'var(--font-size-sm)' }} title={c.change_id}>
              {describeProposal(c)}
            </li>
          ))}
        </ol>
      )}
      {batchVocabularyLabel(batch) ? (
        <Alert variant="info" live={false} id={`sc-vocab-${batch.group_key}`}>
          {batchVocabularyLabel(batch)}
        </Alert>
      ) : null}
      {separateApprovalLabel(batch) ? (
        <Alert variant="info">{`Approve all applies ${batch.batchable}; ${separateApprovalLabel(batch)} (Review rows).`}</Alert>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-2)', flexWrap: 'wrap' }}>
        <Button
          compact
          variant="primary"
          disabled={busy || sampleQuery.isLoading || sample.length === 0}
          aria-describedby={batchVocabularyLabel(batch) ? `sc-vocab-${batch.group_key}` : undefined}
          onClick={() => onApprove(sample.length)}
        >
          {busy ? 'Approving…' : `Approve all ${batch.batchable}`}
        </Button>
        <Button compact variant="secondary" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

const BATCH_STATE_LABEL: Record<ContentBatchRow['state'], string> = {
  ready: 'Ready',
  waiting: 'Waiting',
  held: 'Held',
  individual: 'Review one by one',
};

/**
 * v10.3 Batches view: pending content batches in approval order (core
 * CONTENT_BATCH_ORDER). The server derives each batch's state; this panel
 * renders it and offers the action that state allows. Approve is two-step
 * because one click applies every catalog row in the batch.
 */
/** An in-app return path only (`/w/…`), never another origin. */
function safeReturn(v: string | null): string | null {
  return v && v.startsWith('/') && !v.startsWith('//') && !v.includes(String.fromCharCode(92)) ? v : null;
}

/** Batch approve calls per click: 30 rows each server-side, so up to ~1,500 rows (the Marketplace batch is ~900). */
const MAX_BATCH_ROUNDS = 50;

function BatchesPanel({ onReviewRows, onChanged, onShowSourceActivations, focusKey, returnTo }: {
  /** V106-1-8 / V106-5-6: `?rule_set=<batch key>` (from a program screen or Today) shows that batch first. */
  focusKey?: string | null;
  /** `?return=<path>`: the screen to go back to after approving. */
  returnTo?: string | null;
  onReviewRows: (key: string, label: string) => void;
  onChanged: () => void;
  /** Rows view filtered to pending research-source activations (a missing ingest batch). */
  onShowSourceActivations: () => void;
}) {
  const batchesQuery = useQuery({ queryKey: ['queue-batches'], queryFn: () => listContentBatches() });
  const [confirming, setConfirming] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [result, setResult] = useState<{ variant: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [showAll, setShowAll] = useState(false);

  const approve = async (row: ContentBatchRow, sampled: number) => {
    if (!row.batch_id) return;
    setBusy(row.group_key);
    setResult(null);
    // The server applies a bounded slice per call (30 rows): keep calling until the batch is done, a call
    // applies nothing new (only deferred / failed rows left), or MAX_ROUNDS is reached (2026-10-21).
    let applied = 0;
    let deferred = 0;
    let failed = 0;
    let remaining = 0;
    try {
      for (let round = 0; round < MAX_BATCH_ROUNDS; round++) {
        const r = await approveContentBatch(row.batch_id, {
          review_notes: `Batch sign-off after a spot-check of ${sampled} of ${row.pending} rows (/app/queue).`,
        });
        applied += r.applied;
        deferred = r.deferred.length;
        failed = r.failed.length;
        remaining = r.remaining;
        if (r.remaining > 0 && r.applied > 0) {
          setResult({ variant: 'info', text: `${row.label}: ${applied} applied so far, ${r.remaining} to go…` });
          continue;
        }
        break;
      }
      const extra = [deferred ? `${deferred} deferred` : '', failed ? `${failed} failed` : ''].filter(Boolean).join(', ');
      setResult({
        variant: failed ? 'error' : 'success',
        text: `${row.label}: ${applied} applied${extra ? `, ${extra}` : ''}. ${remaining} still pending.`,
      });
      setConfirming(null);
      onChanged();
    } catch (err) {
      const soFar = applied ? ` (${applied} applied before the error)` : '';
      setResult({ variant: 'error', text: `${err instanceof Error ? err.message : String(err)}${soFar}` });
      if (applied) onChanged();
    } finally {
      setBusy(null);
    }
  };

  if (batchesQuery.isLoading) return <Spinner label="Loading batches" />;
  if (batchesQuery.error) return <Alert variant="error">{batchesQuery.error.message}</Alert>;
  const allBatches = batchesQuery.data?.batches ?? [];
  // `rule_set` names the batch's order entry (`iso-certification`); its group key carries the version (`…@v1`).
  const focused = focusKey && !showAll ? allBatches.filter((b) => b.entry_key === focusKey || b.group_key === focusKey) : [];
  const batches = focused.length ? focused : allBatches;
  const back = returnTo ? <Link to={returnTo}>Back to where you came from</Link> : null;
  if (focusKey && !showAll && !focused.length && result?.variant === 'success') {
    return <div style={{ display: 'grid', gap: 'var(--space-2)' }}><Alert variant="success">{result.text}</Alert>{back}</div>;
  }
  if (batches.length === 0) {
    return <EmptyState title="No pending batches" description="Every content batch has been reviewed." />;
  }

  // Rows arrive in order; consecutive rows with one stage label form a section.
  const stages: Array<{ label: string; rows: ContentBatchRow[] }> = [];
  for (const b of batches) {
    const last = stages[stages.length - 1];
    if (last && last.label === b.stage_label) last.rows.push(b);
    else stages.push({ label: b.stage_label, rows: [b] });
  }

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      {result ? <Alert variant={result.variant}>{result.text}</Alert> : null}
      {focused.length ? (
        <p style={{ margin: 0, display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', alignItems: 'center', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
          {`Showing ${focused[0]!.label}`}
          <Button compact variant="secondary" onClick={() => setShowAll(true)}>Show all batches</Button>
          {back}
        </p>
      ) : null}
      {stages.map((stage) => (
        <section key={stage.label} aria-label={stage.label} style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <h2 style={{ margin: 0, fontSize: 'var(--font-size-md)', color: 'var(--text)' }}>{stage.label}</h2>
          <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: 'var(--space-2)' }}>
            {stage.rows.map((b) => (
              <li key={b.group_key}>
                <Card
                  title={
                    <span style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                      {b.label}
                      <Badge>{b.next ? 'Next' : BATCH_STATE_LABEL[b.state]}</Badge>
                    </span>
                  }
                  right={
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
                  {b.approvable && confirming !== b.group_key ? (
                    <Button compact variant={b.next ? 'primary' : 'secondary'} disabled={busy !== null} onClick={() => setConfirming(b.group_key)}>
                      Spot-check and approve
                    </Button>
                  ) : null}
                  <Button compact variant="secondary" onClick={() => onReviewRows(b.group_key, b.label)}>
                    Review rows
                  </Button>
                </div>
                  }
                >
                  <span style={{ color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                    {b.retry && b.retry === b.pending
                      ? `${b.retry} approved earlier but not applied; approving retries them`
                      : `${b.pending - (b.retry ?? 0)} pending${b.retry ? ` · ${b.retry} approved earlier but not applied (retried on approve)` : ''}`}
                    {b.deferred ? ` · ${b.deferred} deferred` : ''}
                    {separateApprovalLabel(b) ? ` · ${separateApprovalLabel(b)}` : ''}
                    {b.state === 'waiting' ? ` · after ${[...new Set(b.waiting_on.map((w) => w.label))].join(', ')}` : ''}
                    {b.state === 'held' && b.hold_reason ? ` · ${b.hold_reason}` : ''}
                  </span>
                  {/* V109-1-19 R3 (R2-B2): customers wait on this batch ("Couldn't check" until it is approved). */}
                  {b.workspaces_waiting ? (
                    <span data-part="workspaces-waiting" style={{ color: 'var(--text)', fontSize: 'var(--font-size-sm)', fontWeight: 600 }}>
                      {`${b.workspaces_waiting} workspace${b.workspaces_waiting === 1 ? '' : 's'} can't be checked until this is approved`}
                    </span>
                  ) : null}
                  {b.waiting_on.filter((w) => w.missing).map((w) => (
                    <span key={w.group_key} style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap', color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>
                      {w.label} hasn’t been filed yet{w.hint ? `. ${w.hint}.` : '.'}
                      {w.action === 'research_source' ? (
                        <Button compact variant="secondary" onClick={onShowSourceActivations}>
                          Show source activations
                        </Button>
                      ) : null}
                    </span>
                  ))}
                  {b.approvable && confirming === b.group_key ? (
                    <SpotCheck
                      batch={b}
                      busy={busy === b.group_key}
                      onApprove={(sampled) => approve(b, sampled)}
                      onCancel={() => setConfirming(null)}
                    />
                  ) : null}
                </Card>
              </li>
            ))}
          </ol>
        </section>
      ))}
    </div>
  );
}

// --- Sub-components ---

function SummaryStrip({ summary }: { summary: QueueSummary }) {
  const tiles: { label: string; value: number; tone?: 'critical' | 'warning' | 'muted' }[] = [
    { label: 'Pending', value: summary.pending_total },
    { label: 'Critical', value: summary.pending_critical, tone: 'critical' },
    { label: 'Elevated', value: summary.pending_elevated, tone: 'warning' },
    { label: 'Routine', value: summary.pending_routine, tone: 'muted' },
    { label: 'Breaching SLA', value: summary.breaching_sla, tone: 'critical' },
    { label: 'Seed awaiting ratification', value: summary.seed_awaiting_ratification, tone: 'muted' },
  ];
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
        gap: 'var(--space-2)',
      }}
    >
      {tiles.map((t) => (
        <div
          key={t.label}
          style={{
            border: '1px solid var(--border)',
            borderRadius: 'var(--radius-card)',
            padding: 'var(--space-3) var(--space-4)',
            background: 'var(--surface)',
          }}
        >
          <div
            style={{
              fontFamily: 'var(--font-ui)',
              fontSize: 'var(--font-size-3xs)',
              color: 'var(--text-dim)',
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {t.label}
          </div>
          <div
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 'var(--font-size-xl)',
              fontWeight: 600,
              color:
                t.tone === 'critical' && t.value > 0
                  ? 'var(--critical)'
                  : t.tone === 'warning' && t.value > 0
                    ? 'var(--warning)'
                    : 'var(--text)',
            }}
          >
            {t.value}
          </div>
        </div>
      ))}
    </div>
  );
}

function FilterBar(props: {
  status: StatusFilter;
  severity: SeverityFilter;
  source: SourceFilter;
  target: TargetFilter;
  domainFilter: string;
  riskVectorFilter: string;
  breachingOnly: boolean;
  duplicatesOnly: boolean;
  order: OrderFilter;
  onStatus: (v: StatusFilter) => void;
  onSeverity: (v: SeverityFilter) => void;
  onSource: (v: SourceFilter) => void;
  onTarget: (v: TargetFilter) => void;
  onDomainFilter: (v: string) => void;
  onRiskVectorFilter: (v: string) => void;
  onBreachingOnly: (v: boolean) => void;
  onDuplicatesOnly: (v: boolean) => void;
  onOrder: (v: OrderFilter) => void;
  program: ProgramFilter;
  programCounts: Partial<Record<ProposalProgramKey, number>> | null;
  onProgram: (v: ProgramFilter) => void;
}) {
  return (
    <div
      style={{
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-3) var(--space-4)',
        background: 'var(--surface)',
        display: 'grid',
        gap: 'var(--space-3)',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        alignItems: 'end',
      }}
    >
      <Field id="q-program" label="Program">
        <Select
          id="q-program"
          value={props.program}
          onChange={(e) => props.onProgram(e.target.value as ProgramFilter)}
        >
          <option value="all">Any program</option>
          {PROGRAM_KEYS.map((k) => (
            <option key={k} value={k}>
              {PROPOSAL_PROGRAM_LABELS[k]}
              {props.programCounts?.[k] != null ? ` (${props.programCounts[k]!.toLocaleString()} pending)` : ''}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-status" label="Status">
        <Select
          id="q-status"
          value={props.status}
          onChange={(e) => props.onStatus(e.target.value as StatusFilter)}
        >
          {STATUS_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-severity" label="Severity">
        <Select
          id="q-severity"
          value={props.severity}
          onChange={(e) => props.onSeverity(e.target.value as SeverityFilter)}
        >
          {SEVERITY_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-source" label="Source">
        <Select
          id="q-source"
          value={props.source}
          onChange={(e) => props.onSource(e.target.value as SourceFilter)}
        >
          {SOURCE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-target" label="Target">
        <Select
          id="q-target"
          value={props.target}
          onChange={(e) => props.onTarget(e.target.value as TargetFilter)}
        >
          {TARGET_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-domain" label="Domain">
        <Select
          id="q-domain"
          value={props.domainFilter}
          onChange={(e) => props.onDomainFilter(e.target.value)}
        >
          {DOMAIN_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-risk-vector" label="Risk vector">
        <Select
          id="q-risk-vector"
          value={props.riskVectorFilter}
          onChange={(e) => props.onRiskVectorFilter(e.target.value)}
        >
          {RISK_VECTOR_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <Field id="q-order" label="Sort">
        <Select
          id="q-order"
          value={props.order}
          onChange={(e) => props.onOrder(e.target.value as OrderFilter)}
        >
          {ORDER_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </Select>
      </Field>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text-muted)',
          minHeight: 'var(--min-touch-target)',
        }}
      >
        <input
          type="checkbox"
          checked={props.breachingOnly}
          onChange={(e) => props.onBreachingOnly(e.target.checked)}
          style={{ width: 18, height: 18 }}
        />
        Only items breaching SLA
      </label>
      <label
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 'var(--space-2)',
          fontFamily: 'var(--font-ui)',
          fontSize: 'var(--font-size-2xs)',
          color: 'var(--text-muted)',
          minHeight: 'var(--min-touch-target)',
        }}
      >
        <input
          type="checkbox"
          checked={props.duplicatesOnly}
          onChange={(e) => props.onDuplicatesOnly(e.target.checked)}
          style={{ width: 18, height: 18 }}
        />
        Only possible duplicates
      </label>
    </div>
  );
}

function QueueRow(props: {
  change: ProposedChange;
  busy: boolean;
  canWrite: boolean;
  actionState: { kind: 'approve' | 'reject'; notes: string } | null;
  onOpen: (kind: 'approve' | 'reject') => void;
  onCancel: () => void;
  onNotes: (notes: string) => void;
  onConfirm: (kind: 'approve' | 'reject', notes: string, ci?: ControlImpactDecision) => void;
  onDeprecateSource: (reason: string) => void;
  onRejectAndDeprecate: (reason: string) => void;
  onRatify: () => void;
  /** Retry the apply step for a row left approved-but-not-applied. */
  onApply: () => void;
  /** V111-7-6b: turn a matched create into an update of the existing item. */
  onRetarget: (targetName: string, undo?: boolean) => void;
  /** V112-4-7: re-run the Control impact analysis (legal rows); resolves to the new analysis. */
  onRerunImpact: () => Promise<ControlImpactView | null>;
  /** V112-4-7: correct a pending law's jurisdiction. */
  onCorrectJurisdiction: (jurisdiction: string) => Promise<void>;
  /** V112-4-7: add the statute link to a pending law. */
  onSetSourceUrl: (url: string) => Promise<void>;
  /** The page's live region. */
  announce: (message: string) => void;
}) {
  const { change, busy, canWrite, actionState } = props;
  const sla = useSlaCountdown(change);
  const isPending = change.status === 'pending';
  // V112-4-7 (ADR-0098): a law / court decision's suggested Control impact. With an analysis the ticks ARE the
  // decision (amend / new start ticked; the outcome follows them, as on the server). Without one: Re-run, or tick
  // "File a new control for this law". A new analysis (Re-run, jurisdiction / link fix) keeps the editor's ticks for
  // duties whose text is unchanged and starts the others on the suggestion.
  const isLegal = LEGAL_TARGETS.has(change.target_type);
  const ci = useMemo(() => (isLegal ? parseImpact(change.impact).control_impact ?? null : null), [isLegal, change.impact]);
  const ciReady = ci?.status === 'ready';
  const ciKey = ci ? `${ci.status}:${ci.analyzed_at}:${ci.suggested ?? ''}:${ci.duties.length}` : 'none';
  const ciDefaults = useMemo(() => defaultTicks(ci), [ci]);
  const editorTicks = useRef(new Map<string, boolean>());
  const [ciKept, setCiKept] = useState<Set<number>>(() => new Set(ciDefaults));
  const [lawControl, setLawControl] = useState(false);
  useEffect(() => {
    setCiKept(new Set(carryTicks(ci, editorTicks.current)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciKey]);
  const toggleDuty = (i: number, keep: boolean) => {
    if (!ci) return;
    const next = new Set(ciKept);
    if (keep) next.add(i); else next.delete(i);
    editorTicks.current.set(ci.duties[i]!.text, keep);
    setCiKept(next);
    const changed = !sameTicks(next, ciDefaults);
    props.announce(`${changed ? 'You changed the suggestion. ' : ''}Will file: ${willFile(ci.duties, next) ?? 'nothing'}.`);
  };
  const useSuggestion = () => {
    editorTicks.current.clear();
    setCiKept(new Set(ciDefaults));
    if (ci) props.announce(`Back to the suggestion. Will file: ${willFile(ci.duties, ciDefaults) ?? 'nothing'}.`);
  };
  const legalSource = useMemo(() => (isLegal ? legalState(change) : { jurisdiction: null, source_url: null }), [isLegal, change]);
  const approving = actionState?.kind === 'approve';
  // Focus lands on the first tick (what to file), else on Confirm: the Approve button that opened the form is gone.
  useEffect(() => {
    if (!approving || !isLegal) return;
    const t = setTimeout(() => {
      const box = document.getElementById(`ci-${change.change_id}`)?.querySelector<HTMLInputElement>('input[type="checkbox"]');
      (box ?? document.getElementById(`confirm-${change.change_id}`))?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [approving, isLegal, change.change_id]);
  // Without an analysis nothing is recorded unless the editor ticks "File a new control for this law".
  const ciDecision = (): ControlImpactDecision | undefined => (ciReady && ci
    ? { control_impact_outcome: outcomeForTicks(ci.duties, ciKept), control_impact_kept: [...ciKept].sort((a, b) => a - b) }
    : lawControl ? { control_impact_outcome: 'new_control' } : undefined);
  const ciChanged = ciReady && !sameTicks(ciKept, ciDefaults);
  // PR-C0-4 (docs/cluster-0-chrome-sweep-plan.md): human-readable
  // primary title instead of `research_source · src-xyz-123`. The
  // full identifier stays reachable via the row's `title` attribute
  // for auditors + power users; scan-mode readers see the shape of
  // the change, not the ID.
  const humanChangeType =
    change.change_type.charAt(0).toUpperCase() + change.change_type.slice(1);
  const humanTargetType = change.target_type.replace(/_/g, ' ');
  // Plain-language headline (what approving does) + the program it feeds;
  // the change/target type moves to the tertiary id line.
  const primaryTitle = useMemo(() => describeProposal(change), [change]);
  const programKey = useMemo(() => programForProposal(change), [change]);
  const techLine = `${humanChangeType} · ${humanTargetType}${change.target_id ? ` · ${change.target_id}` : ''}`;
  const idHover = change.target_id
    ? `${change.target_type}:${change.target_id} · change ${change.change_id}`
    : `change ${change.change_id}`;
  const rationale = change.ai_rationale.length > 240
    ? `${change.ai_rationale.slice(0, 240).trimEnd()}…`
    : change.ai_rationale;
  const contentUrl = useMemo(() => extractContentUrl(change), [change]);
  const showSourcePanel = isFromNewSource(change);
  // Ratify offered on applied+seed rows whose reviewer stamp is still
  // the placeholder — see ProposedChangeService.ratify() and ADR-0002
  // provenance model. Once ratified, the button disappears.
  const showRatify =
    canWrite &&
    change.status === 'applied' &&
    change.source_kind === 'seed' &&
    change.reviewer === 'seed:v0.1';

  const taxonomy = useMemo(() => {
    if (change.target_type !== 'jurisdiction_rule') return null;
    try {
      const state = JSON.parse(change.proposed_state);
      return {
        domains: Array.isArray(state.rule_domains) ? state.rule_domains as string[] : [],
        riskVectors: Array.isArray(state.risk_vectors) ? state.risk_vectors as string[] : [],
      };
    } catch { return null; }
  }, [change.target_type, change.proposed_state]);

  return (
    <li
      // Escape closes the approve / reject form from anywhere in the row (the ticks sit in the panel above it), except
      // inside an open correction form; focus returns to the button that opened it.
      onKeyDown={(e) => {
        if (e.key !== 'Escape' || !actionState || busy) return;
        if ((e.target as HTMLElement).closest?.('.ci-fix')) return;
        e.stopPropagation();
        const kind = actionState.kind;
        props.onCancel();
        setTimeout(() => document.getElementById(`${kind}-${change.change_id}`)?.focus(), 0);
      }}
      style={{
        border: '1px solid var(--border)',
        borderLeftWidth: '3px',
        borderLeftColor:
          change.severity === 'critical'
            ? 'var(--critical)'
            : change.severity === 'elevated'
              ? 'var(--warning)'
              : 'var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
        padding: 'var(--space-4)',
        display: 'grid',
        gap: 'var(--space-2)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ display: 'grid', gap: '4px', minWidth: 0 }}>
          <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', flexWrap: 'wrap' }}>
            <SeverityChip severity={change.severity} />
            {programKey !== 'other' ? <Badge>{PROPOSAL_PROGRAM_LABELS[programKey]}</Badge> : null}
            <Badge>{change.source_kind}</Badge>
            {change.target_type === 'research_source' ? <SourceProposalChip /> : null}
            {/* Security review M1: the rule quotes law text an editor pasted, not text fetched from the source. */}
            {editorSuppliedText(change) ? <Badge tone="accent">Editor-supplied text</Badge> : null}
            {isFromNewSource(change) ? <NewSourceChip /> : null}
            {taxonomy?.domains.map((d) => (
              <DomainChip key={d} domain={d} />
            ))}
            {taxonomy?.riskVectors.map((v) => (
              <RiskVectorChip key={v} vector={v} />
            ))}
          </div>
          <h2
            style={{
              margin: 0,
              fontSize: 'var(--font-size-md)',
              fontWeight: 600,
              color: 'var(--text)',
            }}
            title={idHover}
            id={`row-title-${change.change_id}`}
            tabIndex={-1}
          >
            {primaryTitle}
          </h2>
          <div
            style={{
              fontSize: 'var(--font-size-3xs)',
              fontFamily: 'var(--font-mono)',
              color: 'var(--text-dim)',
            }}
            // Full identifier line — demoted from primary chrome to
            // tertiary so scan-mode readers focus on the human title
            // above, but auditors can still copy it.
            title={change.change_id}
          >
            {techLine}
          </div>
        </div>
        <div style={{ textAlign: 'right', display: 'grid', gap: '4px', minWidth: '140px' }}>
          <span
            style={{
              fontSize: 'var(--font-size-3xs)',
              color: sla.overdue ? 'var(--critical)' : sla.soon ? 'var(--warning)' : 'var(--text-dim)',
              fontWeight: sla.overdue ? 600 : 500,
            }}
            aria-label={sla.aria}
          >
            <span aria-hidden="true">{sla.overdue ? '⚠ ' : sla.soon ? '△ ' : '· '}</span>
            {sla.text}
          </span>
          <span style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-dim)' }}>
            Raised {fmtRelative(change.raised_at)}
          </span>
        </div>
      </div>
      <SimilarityLine
        change={change}
        busy={busy}
        onRetarget={canWrite && isPending ? props.onRetarget : undefined}
        onRejectDuplicate={canWrite && isPending ? (note) => props.onConfirm('reject', note) : undefined}
      />
      <p style={{ margin: 0, fontSize: 'var(--font-size-2xs)', color: 'var(--text-muted)' }}>
        {rationale}
      </p>
      <ChangeDiff change={change} />
      {contentUrl ? (
        <a
          href={contentUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: 'var(--font-size-2xs)',
            fontFamily: 'var(--font-mono)',
            color: 'var(--accent)',
            wordBreak: 'break-all',
          }}
        >
          {contentUrl}
          <span aria-hidden="true"> ↗</span>
        </a>
      ) : null}
      {change.target_type === 'control_spec' ? <ControlSpecPreview change={change} /> : null}
      {change.target_type === 'vocabulary' ? <VocabularyTermPreview change={change} /> : null}
      <ImpactPreview change={change} />
      {showSourcePanel ? (
        <SourceContextPanel
          change={change}
          busy={busy}
          canWrite={canWrite}
          onDeprecateSource={props.onDeprecateSource}
          onRejectAndDeprecate={props.onRejectAndDeprecate}
        />
      ) : null}
      <ReviewerLine change={change} />
      {isLegal && isPending ? (
        <ControlImpactPanel
          ci={ci}
          lawTitle={primaryTitle}
          jurisdiction={legalSource.jurisdiction}
          sourceUrl={legalSource.source_url}
          onRerun={canWrite ? props.onRerunImpact : undefined}
          onCorrectJurisdiction={canWrite ? props.onCorrectJurisdiction : undefined}
          onSetSourceUrl={canWrite ? props.onSetSourceUrl : undefined}
          announce={props.announce}
          choosing={canWrite && approving}
          kept={ciKept}
          onToggle={toggleDuty}
          lawControl={lawControl}
          onLawControl={setLawControl}
          idPrefix={`ci-${change.change_id}`}
        />
      ) : null}
      {isLegal && !isPending && ci && (ci.filed?.length || ci.skipped?.length) ? (
        <FiledSummary filing={{ filed: ci.filed ?? [], skipped: ci.skipped ?? [] }} duties={ci.duties} />
      ) : null}
      {isPending && canWrite ? (
        <ActionRow
          busy={busy}
          actionState={actionState}
          targetType={change.target_type}
          changeId={change.change_id}
          approveBlocked={isLegal && ci?.status === 'pending'}
          confirmText={isLegal ? (ciReady && ci ? confirmLabel(ci.duties, ciKept) : lawControl ? 'Approve & file 1 new control' : 'Approve') : undefined}
          notesPlaceholder={isLegal ? (ciReady ? 'Optional: why you kept or changed the suggestion' : 'Optional: context for the audit trail') : undefined}
          note={isLegal && ciChanged && ci
            ? <OverrideNote suggested={willFile(ci.duties, ciDefaults)} now={willFile(ci.duties, ciKept)} onUse={useSuggestion} />
            : null}
          onOpen={props.onOpen}
          onCancel={props.onCancel}
          onNotes={props.onNotes}
          onConfirm={(kind, notes) => props.onConfirm(kind, notes, isLegal && kind === 'approve' ? ciDecision() : undefined)}
        />
      ) : null}
      {change.status === 'approved' && canWrite ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center', marginTop: 'var(--space-2)' }}>
          <Badge>Approved · not applied</Badge>
          <Button variant="primary" compact disabled={busy} onClick={props.onApply}>
            {busy ? 'Applying…' : 'Apply now'}
          </Button>
        </div>
      ) : null}
      {change.status === 'approved' && canWrite ? <ApplyFailedLine raw={change.impact} /> : null}
      {showRatify ? (
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            compact
            disabled={busy}
            onClick={props.onRatify}
            title="Replace the seed:v0.1 placeholder reviewer stamp with your identity. No register mutation — audit-trail only."
          >
            {busy ? 'Working…' : 'Ratify (stamp as reviewed)'}
          </Button>
        </div>
      ) : null}
    </li>
  );
}

/** V112-5-7 / ADR-0116: what a new vocabulary term means, in the proposer's words. The heading names the term. */
function VocabularyTermPreview({ change }: { change: ProposedChange }) {
  const term = useMemo(() => {
    try {
      return JSON.parse(change.proposed_state) as { definition?: unknown };
    } catch {
      return null;
    }
  }, [change.proposed_state]);
  if (!term || typeof term.definition !== 'string' || !term.definition) return null;
  return <p style={{ margin: 0, color: 'var(--text-muted)', fontSize: 'var(--font-size-sm)' }}>{term.definition}</p>;
}

function ControlSpecPreview({ change }: { change: ProposedChange }) {
  const spec = useMemo(() => {
    try {
      return JSON.parse(change.proposed_state) as Record<string, unknown>;
    } catch {
      return null;
    }
  }, [change.proposed_state]);

  const excerpts = useMemo(() => {
    if (!change.source_excerpts_json) return [];
    try {
      const parsed = JSON.parse(change.source_excerpts_json);
      return Array.isArray(parsed) ? parsed.filter((e: unknown) => typeof e === 'string') as string[] : [];
    } catch {
      return [];
    }
  }, [change.source_excerpts_json]);

  const [excerptsOpen, setExcerptsOpen] = useState(false);

  if (!spec) return null;

  const isByop = change.extraction_confidence != null;
  const confidence = change.extraction_confidence ?? 0;
  const confidenceColor = confidence >= 0.8 ? 'var(--accent)' : confidence >= 0.5 ? 'var(--warning)' : 'var(--critical)';

  const threshold = spec.threshold_default as Record<string, unknown> | undefined;
  const overrides = spec.framework_overrides as Record<string, Record<string, unknown>> | undefined;
  const sufficiency = spec.sufficiency as Record<string, unknown> | undefined;
  const freshnessSeconds = spec.freshness_max_age_seconds as number | undefined;
  const verdictPolicy = spec.verdict_policy as string | undefined;
  const signals = spec.accepted_signals as Array<Record<string, string>> | undefined;
  const applicability = spec.applicability as Record<string, unknown> | undefined;

  // V112-4-7: plain units ("3 hours", "90 days"), never a rounded "0 days"; an amendment says what it was.
  const freshnessWords = typeof freshnessSeconds === 'number' && freshnessSeconds > 0 ? intervalWords(freshnessSeconds) : null;
  const freshnessBefore = (() => {
    try {
      const cur = change.current_state ? (JSON.parse(change.current_state) as Record<string, unknown>).freshness_max_age_seconds : undefined;
      return typeof cur === 'number' && cur > 0 && cur !== freshnessSeconds ? intervalWords(cur) : null;
    } catch {
      return null;
    }
  })();

  const rows: Array<{ label: string; value: string }> = [];

  if (spec.control_id) rows.push({ label: 'Control', value: String(spec.control_id) });
  if (spec.scope_selector) rows.push({ label: 'Scope', value: String(spec.scope_selector) });

  if (threshold) {
    const metric = threshold.metric ?? '—';
    const max = threshold.max != null ? `≤ ${threshold.max}` : '';
    const min = threshold.min != null ? `≥ ${threshold.min}` : '';
    rows.push({ label: 'Baseline threshold', value: `${metric}: ${max || min}` });
  }

  if (overrides && Object.keys(overrides).length > 0) {
    for (const [fw, t] of Object.entries(overrides)) {
      const max = t.max != null ? `≤ ${t.max}` : '';
      const min = t.min != null ? `≥ ${t.min}` : '';
      rows.push({ label: `Override (${fw})`, value: `${t.metric ?? '—'}: ${max || min}` });
    }
  }

  if (freshnessWords) rows.push({ label: 'Freshness', value: `evidence at least every ${freshnessWords}${freshnessBefore ? ` (now ${freshnessBefore})` : ''}` });

  if (sufficiency) {
    const parts: string[] = [];
    if (sufficiency.min_samples != null) parts.push(`${sufficiency.min_samples} samples`);
    if (sufficiency.min_samples_per_subgroup != null) parts.push(`${sufficiency.min_samples_per_subgroup}/subgroup`);
    if (sufficiency.min_coverage_ratio != null) parts.push(`${((sufficiency.min_coverage_ratio as number) * 100).toFixed(0)}% coverage`);
    if (parts.length > 0) rows.push({ label: 'Sufficiency', value: parts.join(', ') });
  }

  if (verdictPolicy) rows.push({ label: 'Verdict policy', value: verdictPolicy.replace(/_/g, ' ') });

  if (signals && signals.length > 0) {
    const tools = signals.map((s) => s.tool ?? s.check_key ?? '?').join(', ');
    rows.push({ label: 'Accepted signals', value: tools });
  }

  if (applicability && Object.keys(applicability).length > 0) {
    const classes = (applicability.component_class as string[]) ?? [];
    if (classes.length > 0) rows.push({ label: 'Applies to', value: classes.join(', ') });
  }

  if (rows.length === 0 && !isByop) return null;

  return (
    <div
      style={{
        background: 'var(--surface-elevated)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-3)',
        display: 'grid',
        gap: 'var(--space-1)',
        fontSize: 'var(--font-size-2xs)',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 600, color: 'var(--text)', fontSize: 'var(--font-size-xs)', marginBottom: 'var(--space-1)' }}>
        Control specification
        <ControlPolicyStatusToken status={spec.approved_by === 's-nielsen' ? 'spec_active' : 'spec_pending_review'} />
        {isByop ? (
          <span style={{
            display: 'inline-block', padding: '1px 6px',
            borderRadius: 'var(--radius-chip)',
            background: 'var(--accent-soft, var(--surface-elevated))',
            color: 'var(--accent)',
            fontSize: 'var(--font-size-3xs)', fontWeight: 500,
          }}>Customer upload</span>
        ) : (
          <Badge>{spec.approved_by === 's-nielsen' ? 'baseline' : 'custom'}</Badge>
        )}
      </div>
      {isByop && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-1)' }}>
          <span style={{ color: confidenceColor, fontWeight: 600 }}>
            {Math.round(confidence * 100)}% confidence
          </span>
          {change.extraction_source && (
            <span style={{ color: 'var(--text-dim)' }}>
              from {change.extraction_source}
            </span>
          )}
        </div>
      )}
      {rows.map((r, i) => (
        <div key={i} style={{ display: 'flex', gap: 'var(--space-3)' }}>
          <span style={{ color: 'var(--text-dim)', minWidth: '140px', flexShrink: 0 }}>{r.label}</span>
          <span style={{ color: 'var(--text)', fontFamily: r.label === 'Control' ? 'var(--font-mono)' : undefined }}>{r.value}</span>
        </div>
      ))}
      {isByop && excerpts.length > 0 && (
        <div style={{ marginTop: 'var(--space-1)' }}>
          <button
            onClick={() => setExcerptsOpen(!excerptsOpen)}
            style={{
              background: 'none', border: 'none', padding: 0, cursor: 'pointer',
              color: 'var(--accent)', fontSize: 'var(--font-size-3xs)',
              fontFamily: 'var(--font-ui)',
            }}
          >
            {excerptsOpen ? 'Hide' : 'Show'} source excerpts ({excerpts.length})
          </button>
          {excerptsOpen && (
            <div style={{
              marginTop: 'var(--space-1)', display: 'grid', gap: 'var(--space-1)',
              padding: 'var(--space-2)', background: 'var(--surface)',
              border: '1px solid var(--border)', borderRadius: 'var(--radius-card)',
              fontSize: 'var(--font-size-3xs)',
            }}>
              {excerpts.map((excerpt, i) => (
                <blockquote key={i} style={{
                  margin: 0, paddingLeft: 'var(--space-2)',
                  borderLeft: '2px solid var(--accent)',
                  fontStyle: 'italic', color: 'var(--text-muted)',
                }}>
                  {excerpt}
                </blockquote>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ImpactPreview({ change }: { change: ProposedChange }) {
  const impact = useMemo(() => parseImpact(change.impact), [change.impact]);
  const chips: { label: string; tone?: 'warning' }[] = [];
  if (typeof impact.tenants_affected === 'number') {
    chips.push({
      label: `${impact.tenants_affected} tenant${impact.tenants_affected === 1 ? '' : 's'}`,
      tone: impact.tenants_affected > 0 ? 'warning' : undefined,
    });
  }
  if (typeof impact.register_items_affected === 'number') {
    chips.push({
      label: `${impact.register_items_affected} register item${impact.register_items_affected === 1 ? '' : 's'}`,
    });
  }
  if (Array.isArray(impact.frameworks_affected) && impact.frameworks_affected.length > 0) {
    const preview = impact.frameworks_affected.slice(0, 3).join(', ');
    const extra = impact.frameworks_affected.length - 3;
    chips.push({ label: extra > 0 ? `${preview} +${extra}` : preview });
  }
  if (chips.length === 0 && !impact.severity_rationale) return null;
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--space-2)',
        alignItems: 'center',
        flexWrap: 'wrap',
        fontSize: 'var(--font-size-3xs)',
        color: 'var(--text-dim)',
        fontFamily: 'var(--font-ui)',
      }}
    >
      <span style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}>Impact</span>
      {chips.map((c, i) => (
        <span
          key={i}
          style={{
            padding: '1px 6px',
            borderRadius: 'var(--radius-chip)',
            border: '1px solid var(--border)',
            background: 'var(--surface)',
            color: c.tone === 'warning' ? 'var(--warning)' : 'var(--text-muted)',
            fontFamily: 'var(--font-mono)',
          }}
        >
          {c.label}
        </span>
      ))}
      {impact.severity_rationale ? (
        <span style={{ color: 'var(--text-muted)', fontStyle: 'italic' }}>
          — {impact.severity_rationale}
        </span>
      ) : null}
    </div>
  );
}

type ImpactShape = {
  /** V112-4-7 (ADR-0098): a law / court decision's suggested Control impact (normalised by parseImpact). */
  control_impact?: ControlImpactView;
  /** V112-4-6: a follow-on proposal's law (it was filed by that law's Control impact). */
  source_change_id?: string;
  /** V113-7-6: the last apply of this approved row threw (single or batch path); cleared by a successful apply. */
  apply_failed?: { error?: string; at?: string };
  tenants_affected?: number;
  register_items_affected?: number;
  frameworks_affected?: string[];
  severity_rationale?: string;
  /** V111-7-5: the variant detector's verdict for a scanner finding. */
  similarity?: {
    decision: 'variant' | 'similar' | 'none' | 'duplicate';
    matched_target_type: string | null;
    matched_target_id: string | null;
    matched_title?: string | null;
    score: number | null;
    method: 'rerank' | 'embedding' | 'exact_key';
    /** V112-2-10 (duplicate): the matched proposal, and the key that matched in plain words. */
    matched_change_id?: string | null;
    match_key?: string;
    /** V111-7-6b: set when a reviewer turned a "similar" create into this update. */
    retargeted_by?: string;
    retargeted_at?: string;
  };
};

/**
 * V111-7-6: how this finding relates to existing content. "Updates existing"
 * (the detector filed it as an update) or "May be the same as" (a weaker
 * match the reviewer should check). Words + glyph carry it, never color alone.
 */
function SimilarityLine({ change, busy, onRetarget, onRejectDuplicate }: { change: ProposedChange; busy?: boolean; onRetarget?: (targetName: string, undo?: boolean) => void; onRejectDuplicate?: (note: string) => void }) {
  const sim = useMemo(() => parseImpact(change.impact).similarity, [change.impact]);
  const byReviewer = sim?.retargeted_by ?? null;
  // V112-2-10 (owner PD-3): an exact key matched another proposal or a live item. Flagged, never blocked:
  // the reviewer rejects it in one step (the note names what it duplicates).
  if (sim?.decision === 'duplicate') {
    const what = sim.matched_title || sim.matched_change_id || sim.matched_target_id || 'an existing item';
    const where = sim.matched_change_id ? 'proposal' : 'in the register';
    return (
      <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap', fontSize: 'var(--font-size-2xs)', color: 'var(--text)' }}>
        <span aria-hidden="true">=</span>
        <span style={{ fontWeight: 600 }}>Possible duplicate of</span>
        <span>{what}</span>
        <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>{[where, sim.match_key].filter(Boolean).join(' · ')}</span>
        {onRejectDuplicate ? (
          <Button
            variant="secondary"
            compact
            aria-disabled={busy || undefined}
            aria-describedby={`row-title-${change.change_id}`}
            onClick={() => { if (!busy) onRejectDuplicate(`Duplicate of ${what}${sim.matched_change_id ? ` (${sim.matched_change_id})` : ''}: ${sim.match_key ?? 'same key'}.`); }}
          >
            {busy ? 'Updating…' : <>Reject as duplicate<span className="sr-only">: {what}</span></>}
          </Button>
        ) : null}
      </div>
    );
  }
  if (!sim || sim.decision === 'none' || !sim.matched_target_id) return null;
  const kind = (sim.matched_target_type ?? change.target_type).replace(/_/g, ' ');
  const named = !!sim.matched_title;
  const target = named ? sim.matched_title! : `${kind} ${sim.matched_target_id}`;
  const pct = sim.score != null ? `${Math.round(sim.score * 100)}% match` : null;
  const how = sim.method === 'rerank' ? 'confirmed by review model' : 'by text similarity';
  // A reviewer-retargeted "similar" match is now an update of that item too.
  const variant = sim.decision === 'variant' || change.change_type === 'update';
  return (
    <div
      style={{
        display: 'flex', gap: 'var(--space-2)', alignItems: 'baseline', flexWrap: 'wrap',
        fontSize: 'var(--font-size-2xs)', color: 'var(--text)',
      }}
    >
      <span aria-hidden="true">{variant ? '↻' : '≈'}</span>
      <span style={{ fontWeight: 600 }}>{variant ? 'Updates existing' : 'May be the same as'}</span>
      <span style={named ? undefined : { fontFamily: 'var(--font-mono)' }}>{target}</span>
      <span style={{ color: 'var(--text-dim)', fontSize: 'var(--font-size-3xs)' }}>
        {[named ? kind : null, pct, how, byReviewer ? `changed to an update by ${byReviewer}${sim.retargeted_at ? ` ${fmtRelative(sim.retargeted_at)}` : ''}` : null].filter(Boolean).join(' · ')}
      </span>
      {onRetarget && byReviewer && change.change_type === 'update' ? (
        <Button
          id={`retarget-${change.change_id}`}
          variant="ghost"
          compact
          aria-disabled={busy || undefined}
          aria-describedby={`row-title-${change.change_id}`}
          onClick={() => { if (!busy) onRetarget(target, true); }}
        >
          {busy ? 'Updating…' : <>Keep as a new item instead<span className="sr-only">: leave {target} unchanged</span></>}
        </Button>
      ) : null}
      {onRetarget && !variant && change.change_type === 'create' && !change.target_id ? (
        <Button
          id={`retarget-${change.change_id}`}
          variant="secondary"
          compact
          aria-disabled={busy || undefined}
          aria-describedby={`row-title-${change.change_id}`}
          onClick={() => { if (!busy) onRetarget(target); }}
        >
          {busy ? 'Updating…' : <>Update {named ? 'it' : 'that item'} instead<span className="sr-only">: {target}</span></>}
        </Button>
      ) : null}
    </div>
  );
}

/** Fields the diff never shows (bookkeeping, not content). */
const DIFF_SKIP = new Set(['created_at', 'updated_at', 'reviewed_at', 'version']);

/**
 * V111-7-6: before/after for an update — only the fields that change. The
 * snapshot (`current_state`) is captured when the proposal is filed, so this
 * is what approving would change at that moment.
 */
function ChangeDiff({ change }: { change: ProposedChange }) {
  const rows = useMemo(() => {
    if (change.change_type !== 'update' || !change.current_state) return [];
    let before: Record<string, unknown>;
    let after: Record<string, unknown>;
    try {
      before = JSON.parse(change.current_state) as Record<string, unknown>;
      after = JSON.parse(change.proposed_state) as Record<string, unknown>;
    } catch {
      return [];
    }
    return Object.keys(after)
      .filter((k) => !DIFF_SKIP.has(k) && JSON.stringify(before[k] ?? null) !== JSON.stringify(after[k] ?? null))
      .map((k) => ({ field: k, before: show(before[k]), after: show(after[k]) }));
  }, [change.change_type, change.current_state, change.proposed_state]);
  // A reviewer just turned this into an update: show what it would overwrite.
  const retargetedAt = parseImpact(change.impact).similarity?.retargeted_at ?? null;
  const [open, setOpen] = useState(!!retargetedAt);
  useEffect(() => { if (retargetedAt) setOpen(true); }, [retargetedAt]);
  if (rows.length === 0) return null;
  return (
    <details open={open} style={{ fontSize: 'var(--font-size-2xs)' }} onToggle={(e) => setOpen(e.currentTarget.open)}>
      <summary id={`diff-${change.change_id}`} style={{ cursor: 'pointer', color: 'var(--text)', fontWeight: 600, minHeight: '44px', display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <span aria-hidden="true">{open ? '▾' : '▸'}</span>
        What changes ({rows.length} field{rows.length === 1 ? '' : 's'})
      </summary>
      <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: 'var(--space-2)' }}>
        <thead>
          <tr>
            {['Field', 'Now', 'After approval'].map((h) => (
              <th key={h} scope="col" style={{ textAlign: 'left', padding: 'var(--space-1) var(--space-2)', color: 'var(--text-dim)', fontWeight: 500, borderBottom: '1px solid var(--border)' }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.field}>
              <th scope="row" style={{ textAlign: 'left', verticalAlign: 'top', padding: 'var(--space-1) var(--space-2)', fontWeight: 500, color: 'var(--text-muted)' }}>{fieldLabel(r.field)}</th>
              <td style={{ verticalAlign: 'top', padding: 'var(--space-1) var(--space-2)', color: 'var(--text-muted)', wordBreak: 'break-word' }}>
                <span className="sr-only">Now: </span>{r.before}
              </td>
              <td style={{ verticalAlign: 'top', padding: 'var(--space-1) var(--space-2)', color: 'var(--text)', fontWeight: 500, wordBreak: 'break-word' }}>
                <span className="sr-only">After approval: </span>{r.after}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </details>
  );
}

function fieldLabel(k: string): string {
  const s = k.replace(/_(id|json)$/, '').replace(/_/g, ' ');
  return s.charAt(0).toUpperCase() + s.slice(1);
}

function show(v: unknown): string {
  if (v === null || v === undefined || v === '') return '—';
  if (typeof v === 'string') return v.length > 280 ? `${v.slice(0, 280)}…` : v;
  const s = JSON.stringify(v);
  return s.length > 280 ? `${s.slice(0, 280)}…` : s;
}

/** V113-7-6: why the last apply failed, under the "Approved · not applied" badge (nothing when none is recorded). */
function ApplyFailedLine({ raw }: { raw: string }) {
  const failed = parseImpact(raw).apply_failed;
  if (!failed?.error) return null;
  return (
    <p data-part="apply-failed" role="note" style={{ margin: 'var(--space-1) 0 0', fontSize: 'var(--font-size-3xs)', color: 'var(--danger-fg)', overflowWrap: 'anywhere' }}>
      {`Apply failed${failed.at ? ` ${fmtDate(failed.at)}` : ''}: ${failed.error}`}
    </p>
  );
}

function parseImpact(raw: string): ImpactShape {
  try {
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      const shape = parsed as ImpactShape;
      if ('control_impact' in shape) shape.control_impact = normalizeControlImpact(shape.control_impact) ?? undefined;
      return shape;
    }
  } catch {
    /* fall through */
  }
  return {};
}

function ReviewerLine({ change }: { change: ProposedChange }) {
  if (!change.reviewer) return null;
  const seeded = change.reviewer === 'seed:v0.1';
  return (
    <div
      style={{
        fontSize: 'var(--font-size-3xs)',
        color: 'var(--text-dim)',
        fontFamily: 'var(--font-mono)',
      }}
    >
      Reviewed by <strong style={{ color: seeded ? 'var(--text-dim)' : 'var(--text-muted)' }}>{change.reviewer}</strong>
      {change.reviewed_at ? <> · {fmtRelative(change.reviewed_at)}</> : null}
      {seeded ? <span style={{ marginLeft: 'var(--space-2)' }}>· awaiting your ratification</span> : null}
    </div>
  );
}

/**
 * True when the change's origin source was itself approved within the
 * NEW_SOURCE_WINDOW_MS probation window — signals to the reviewer that
 * the underlying feed is still on trial and its output deserves an
 * extra look. Content-only: research_source proposals wear the
 * SourceProposalChip instead (they ARE the source-review, not FROM one).
 */
function isFromNewSource(change: ProposedChange): boolean {
  if (change.target_type === 'research_source') return false;
  if (!change.source_approved_at) return false;
  const approved = Date.parse(change.source_approved_at);
  if (Number.isNaN(approved)) return false;
  return Date.now() - approved < NEW_SOURCE_WINDOW_MS;
}

/**
 * Inline source-context panel — shown under the impact preview on any
 * content proposal wearing the new-source chip. Puts the source's
 * identity and a Deprecate action in the same row as the content review
 * so a reviewer can act on both without navigating to a separate source
 * list. The deprecate action requires a written reason (audit-trail
 * hygiene, and future source-discovery workflows will use it as a
 * signal to skip re-proposing the same URL).
 */
function SourceContextPanel(props: {
  change: ProposedChange;
  busy: boolean;
  canWrite: boolean;
  onDeprecateSource: (reason: string) => void;
  onRejectAndDeprecate: (reason: string) => void;
}) {
  const { change, busy, canWrite } = props;
  // pendingAction distinguishes the two writes the reason applies to:
  // 'deprecate' → source-only; 'cascade' → reject content AND deprecate
  // source. Same textarea, different confirm handler.
  const [pendingAction, setPendingAction] = useState<null | 'deprecate' | 'cascade'>(null);
  const [reason, setReason] = useState('');
  const approvedAt = change.source_approved_at ? Date.parse(change.source_approved_at) : NaN;
  const probationRemaining = Number.isNaN(approvedAt)
    ? null
    : Math.max(0, NEW_SOURCE_WINDOW_MS - (Date.now() - approvedAt));
  const probationLabel = probationRemaining === null
    ? ''
    : probationRemaining === 0
      ? 'probation ended'
      : `${fmtDuration(probationRemaining)} of probation left`;
  return (
    <div
      style={{
        border: '1px solid var(--warning)',
        borderRadius: 'var(--radius-card)',
        padding: 'var(--space-2) var(--space-3)',
        background: 'var(--warning-soft, var(--surface-elevated, var(--surface)))',
        display: 'grid',
        gap: 'var(--space-2)',
      }}
    >
      <div
        style={{
          display: 'flex',
          gap: 'var(--space-2)',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          fontSize: 'var(--font-size-3xs)',
          fontFamily: 'var(--font-ui)',
          color: 'var(--text-muted)',
        }}
      >
        <div style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
          <div style={{ color: 'var(--text)', fontWeight: 600 }}>
            {change.source_name ?? 'Unknown source'}
            {change.research_source_kind ? (
              <span style={{ marginLeft: 'var(--space-2)', color: 'var(--text-dim)', fontFamily: 'var(--font-mono)' }}>
                {change.research_source_kind}
              </span>
            ) : null}
          </div>
          <div style={{ color: 'var(--text-dim)' }}>
            {change.source_url ? (
              <a
                href={change.source_url}
                target="_blank"
                rel="noopener noreferrer"
                style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}
              >
                {change.source_url}
              </a>
            ) : null}
            {change.source_approved_at ? (
              <span style={{ marginLeft: change.source_url ? 'var(--space-2)' : 0 }}>
                approved {fmtRelative(change.source_approved_at)} · {probationLabel}
              </span>
            ) : null}
          </div>
        </div>
        {change.status === 'pending' && canWrite && pendingAction === null ? (
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="secondary"
              compact
              disabled={busy}
              onClick={() => setPendingAction('deprecate')}
            >
              Deprecate source
            </Button>
            <Button
              variant="secondary"
              compact
              disabled={busy}
              onClick={() => setPendingAction('cascade')}
              title="Reject this content AND deprecate its source in one step. Both actions share the reason and land in the audit trail."
            >
              Reject + deprecate source
            </Button>
          </div>
        ) : null}
      </div>
      {pendingAction !== null ? (
        <div style={{ display: 'grid', gap: 'var(--space-2)' }}>
          <Field
            id={`src-action-${change.change_id}`}
            label={
              pendingAction === 'cascade'
                ? 'Why reject this content AND remove its source?'
                : 'Why remove this source?'
            }
          >
            <Textarea
              id={`src-action-${change.change_id}`}
              rows={2}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={
                pendingAction === 'cascade'
                  ? 'Required — used for BOTH the content rejection and the source deprecation. e.g. Source is a hallucination factory; every proposal so far has cited pages that don’t exist.'
                  : 'Required — e.g. URL now behind login; feed produces off-topic content; superseded by another source.'
              }
            />
          </Field>
          <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
            <Button
              variant="primary"
              compact
              disabled={busy || !reason.trim()}
              onClick={() => {
                const r = reason.trim();
                if (pendingAction === 'cascade') {
                  props.onRejectAndDeprecate(r);
                } else {
                  props.onDeprecateSource(r);
                }
                setPendingAction(null);
                setReason('');
              }}
            >
              {busy
                ? 'Working…'
                : pendingAction === 'cascade'
                  ? 'Confirm reject + deprecate'
                  : 'Confirm deprecate source'}
            </Button>
            <Button
              variant="ghost"
              compact
              disabled={busy}
              onClick={() => {
                setPendingAction(null);
                setReason('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SourceProposalChip() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: 'var(--radius-chip)',
        background: 'var(--accent-soft, var(--surface-elevated, var(--surface)))',
        color: 'var(--accent, var(--text))',
        border: '1px solid var(--accent, var(--border))',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
      title="This proposal adds or deprecates a research source. Approving activates the source for the next cron tick; rejecting leaves it inactive."
    >
      source review
    </span>
  );
}

function NewSourceChip() {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: 'var(--radius-chip)',
        background: 'var(--warning-soft, var(--surface-elevated, var(--surface)))',
        color: 'var(--warning)',
        border: '1px solid var(--warning)',
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
      title="This finding came from a source that was approved less than 30 days ago. The source is on editorial probation — weigh trustworthiness before approving downstream content."
    >
      new source
    </span>
  );
}

function SeverityChip({ severity }: { severity: ProposedChangeSeverity }) {
  const tone = SEVERITY_TONE[severity];
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        padding: '1px 6px',
        borderRadius: 'var(--radius-chip)',
        background: tone.bg,
        color: tone.fg,
        fontFamily: 'var(--font-mono)',
        fontSize: 'var(--font-size-3xs)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
      }}
    >
      {tone.label}
    </span>
  );
}

const LEGAL_TARGETS = new Set(['case_law', 'legislation']);
/** The jurisdiction a law was filed under (for the "Filed as US, but…" flag) and its source link. */
function legalState(change: ProposedChange): { jurisdiction: string | null; source_url: string | null } {
  try {
    const state = JSON.parse(change.proposed_state) as { jurisdiction?: unknown; source_url?: unknown };
    return {
      jurisdiction: typeof state.jurisdiction === 'string' ? state.jurisdiction : null,
      source_url: typeof state.source_url === 'string' && /^https?:\/\//.test(state.source_url) ? state.source_url : null,
    };
  } catch {
    return { jurisdiction: null, source_url: null };
  }
}

function ActionRow(props: {
  busy: boolean;
  actionState: { kind: 'approve' | 'reject'; notes: string } | null;
  targetType: string;
  changeId: string;
  /** Approve is unavailable while the analysis is running. */
  approveBlocked?: boolean;
  /** The Confirm label naming what approving files. */
  confirmText?: string;
  notesPlaceholder?: string;
  /** Shown above the notes (override / nothing-filed). */
  note?: ReactNode;
  onOpen: (kind: 'approve' | 'reject') => void;
  onCancel: () => void;
  onNotes: (notes: string) => void;
  onConfirm: (kind: 'approve' | 'reject', notes: string) => void;
}) {
  const { actionState, busy } = props;

  if (!actionState) {
    return (
      <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
        <Button id={`approve-${props.changeId}`} variant="primary" compact disabled={busy || props.approveBlocked} onClick={() => props.onOpen('approve')}>
          {props.approveBlocked ? 'Approve (analysis running)' : 'Approve & apply'}
        </Button>
        <Button id={`reject-${props.changeId}`} variant="secondary" compact disabled={busy} onClick={() => props.onOpen('reject')}>
          Reject
        </Button>
      </div>
    );
  }
  return (
    <div style={{ display: 'grid', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
      {actionState.kind === 'approve' ? props.note : null}
      <Field
        id={`notes-${actionState.kind}`}
        label={actionState.kind === 'approve' ? 'Approval notes (optional)' : 'Reason for rejection'}
      >
        <Textarea
          id={`notes-${actionState.kind}`}
          rows={2}
          value={actionState.notes}
          onChange={(e) => props.onNotes(e.target.value)}
          placeholder={
            actionState.kind === 'approve'
              ? props.notesPlaceholder ?? 'Optional context for the audit trail'
              : 'Required: why is this rejected?'
          }
        />
      </Field>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <Button
          id={`confirm-${props.changeId}`}
          variant="primary"
          compact
          disabled={busy || (actionState.kind === 'reject' && !actionState.notes.trim())}
          onClick={() => props.onConfirm(actionState.kind, actionState.notes)}
        >
          {busy ? 'Working…' : actionState.kind === 'approve' ? props.confirmText ?? 'Confirm approve & apply' : 'Confirm reject'}
        </Button>
        <Button variant="ghost" compact disabled={busy} onClick={props.onCancel}>
          Cancel
        </Button>
      </div>
    </div>
  );
}

// --- Proposed-state helpers ---

function extractContentUrl(change: ProposedChange): string | null {
  try {
    const state = JSON.parse(change.proposed_state);
    const raw = state.source_url ?? state.url;
    if (typeof raw === 'string' && /^https?:\/\//.test(raw)) return raw;
  } catch {
    // malformed JSON — treat as no URL
  }
  return null;
}

// --- Time helpers ---

function useSlaCountdown(change: ProposedChange): { text: string; overdue: boolean; soon: boolean; aria: string } {
  return useMemo(() => {
    if (change.status !== 'pending') {
      return {
        text: change.status === 'applied' ? `Applied ${fmtRelative(change.applied_at ?? change.reviewed_at ?? change.created_at)}` : `${change.status[0].toUpperCase()}${change.status.slice(1)}`,
        overdue: false,
        soon: false,
        aria: change.status,
      };
    }
    const now = Date.now();
    const deadline = Date.parse(change.sla_deadline_at);
    if (Number.isNaN(deadline)) {
      return { text: 'No deadline', overdue: false, soon: false, aria: 'no deadline' };
    }
    const ms = deadline - now;
    const overdue = ms < 0;
    const abs = Math.abs(ms);
    const label = fmtDuration(abs);
    const text = overdue ? `SLA breached · ${label} overdue` : `SLA in ${label}`;
    const soon = !overdue && ms < 24 * 60 * 60 * 1000;
    return { text, overdue, soon, aria: text };
  }, [change]);
}

function fmtDuration(ms: number): string {
  const minutes = Math.round(ms / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${hours}h`;
  const days = Math.round(hours / 24);
  return `${days}d`;
}

function fmtRelative(iso: string): string {
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return iso;
  const diff = Date.now() - t;
  const past = diff >= 0;
  const abs = Math.abs(diff);
  const label = fmtDuration(abs);
  return past ? `${label} ago` : `in ${label}`;
}

// --- Taxonomy chips (V94-4-3) ---

function DomainChip({ domain }: { domain: string }) {
  const label = RULE_DOMAIN_LABELS[domain as RuleDomain] ?? domain.replace(/_/g, ' ');
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '1px 6px',
      borderRadius: 'var(--radius-chip)',
      background: 'var(--surface-elevated, var(--surface))',
      color: 'var(--text-muted)',
      border: '1px solid var(--border)',
      fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-3xs)',
    }}>
      {label}
    </span>
  );
}

function RiskVectorChip({ vector }: { vector: string }) {
  const label = RISK_VECTOR_LABELS[vector as RiskVector] ?? vector.replace(/_/g, ' ');
  const category = RISK_VECTOR_CATEGORIES[vector as RiskVector];
  const style = category === 'harm_to_people'
    ? { color: 'var(--warning)', bg: 'var(--warning-soft, var(--surface-elevated, var(--surface)))' }
    : category === 'harm_to_ecosystems'
      ? { color: 'var(--info, var(--accent))', bg: 'var(--accent-soft, var(--surface-elevated, var(--surface)))' }
      : { color: 'var(--text-muted)', bg: 'var(--surface-elevated, var(--surface))' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', padding: '1px 6px',
      borderRadius: 'var(--radius-chip)',
      background: style.bg, color: style.color,
      border: '1px solid var(--border)',
      fontFamily: 'var(--font-ui)', fontSize: 'var(--font-size-3xs)',
    }}>
      {label}
    </span>
  );
}
